import { MarkdownRenderer } from "@/components/MarkdownRenderer";
import {
  BodyText,
  Button,
  Callout,
  Collapsible,
  Skeleton,
  toast,
} from "@/components/ui";
import { TRACKERS } from "@/constants/Trackers";
import { EditableRecordButton } from "@/features/Patient/MedicalRecords/components";
import { trackEvent } from "@/lib/tracking/tracking";
import { hasMarkdownFormatting } from "@/lib/utils";
import { useEncounter } from "@/network/useEncounter";
import { isActionAvailable } from "@/utils/available-actions";
import { useParams, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import type { z } from "zod";
import {
  ChartingAvailableAction,
  useChartingAvailableActions,
} from "../network/useChartingAvailableActions";
import { useCreateSubjective } from "../network/useCreateSubjective";
import { useSubjective } from "../network/useSubjective";
import { useUpdateSubjective } from "../network/useUpdateSubjective";
import { AVCallSummaryAddendums } from "./AVCallSummaryAddendums";
import { AmazonCiq } from "./AmazonCiq/AmazonCiq";
import { AmazonVisitHistory } from "./AmazonVisitHistory/AmazonVisitHistory";
import { SubjectiveAddendums } from "./SubjectiveAddendums";
import { SubjectiveEditor } from "./SubjectiveEditor";
import type { editSubjectiveSchema } from "./schemas";
import { updateSubjectiveSummaryTag } from "./subjectiveText";
import type { Addendum } from "./types";

export const Subjective = () => {
  const searchParams = useSearch({ from: "/patient/$patientId" });
  const { patientId } = useParams({ from: "/patient/$patientId" });
  const encounterId = searchParams.encounterId as string; //  `Chart` is conditionally rendered by presence of encounterId
  const [open, setOpen] = useState(true);
  const { data, isFetching, isError } = useSubjective(encounterId);

  const createSubjective = useCreateSubjective({ patientId, encounterId });
  const updateSubjective = useUpdateSubjective({
    patientId,
    encounterId,
    docRefId: data?.documentReferences[0]?.id,
  });

  const [isEditing, setIsEditing] = useState(false);
  const { data: chartingAvailableActions } = useChartingAvailableActions({
    patientId,
    motherEncounterId: encounterId as string,
  });

  // Nori-style addendums identified by the partner identifier sub-extension
  // (sent to charting as `addedAddendumIds`).
  const [noriAddendums, setNoriAddendums] = useState<Addendum[]>([]);
  const handleAddNoriAddendum = (addendum: Addendum) => {
    setNoriAddendums((prev) => [...prev, addendum]);
    setIsEditing(true);
  };

  // AV call summary addendums identified by the metadata extension's `id`
  // (sent to charting as `addedAddendumExtensionIds`).
  const [avCallAddendums, setAvCallAddendums] = useState<Addendum[]>([]);
  const handleAddAvCallAddendum = (addendum: Addendum) => {
    setAvCallAddendums((prev) => [...prev, addendum]);
    setIsEditing(true);
  };

  const { data: encounter } = useEncounter(encounterId, patientId);

  const getVisitTypeLabel = () => {
    if (encounter?.isAsyncDiagnosis) return "ASYNC DX";
    if (encounter?.isRxRequest) return "ASYNC RX";
    if (encounter?.isRxRenewalRequest) return "RX RENEWAL";
    return undefined;
  };
  const visitTypeLabel = getVisitTypeLabel();

  const canWriteSubjective = isActionAvailable(
    ChartingAvailableAction.WRITE_SUBJECTIVE,
    chartingAvailableActions,
  );
  const canReadSubjective =
    canWriteSubjective ||
    isActionAvailable(
      ChartingAvailableAction.READ_SUBJECTIVE,
      chartingAvailableActions,
    );

  if (!canReadSubjective) {
    return null;
  }

  const isLoading = updateSubjective.isPending;

  // Let the mutation throw — the editor's catch block keeps the form dirty
  // so the user has a recovery path via the Save button.
  const handleAutoSave = async (text: string) => {
    if (!data?.documentReferences[0]?.id) return;
    await updateSubjective.mutateAsync({
      subjectiveText: text,
      addedAddendumIds: noriAddendums.map((a) => a.id),
      addedAddendumExtensionIds: avCallAddendums.map((a) => a.id),
    });
    setNoriAddendums([]);
    setAvCallAddendums([]);
  };

  const onSubmit = async (values: z.infer<typeof editSubjectiveSchema>) => {
    trackEvent(TRACKERS.DOCUMENT_SUBJECTIVE);
    try {
      await updateSubjective.mutateAsync({
        subjectiveText: values.text,
        addedAddendumIds: noriAddendums.map((a) => a.id),
        addedAddendumExtensionIds: avCallAddendums.map((a) => a.id),
      });
      setIsEditing(false);
      setNoriAddendums([]);
      setAvCallAddendums([]);
    } catch (error) {
      toast.error("Failed to update subjective information");
    }
  };

  const handleClickAdd = async () => {
    try {
      await createSubjective.mutateAsync({
        encounterId,
        patientId,
      });
    } catch (error) {
      toast.error("Failed to add subjective information");
    }
  };

  if (isError) {
    return (
      <Callout
        className="w-full"
        variant="danger"
        title="Something went wrong!"
        description="Failed to load subjective."
      />
    );
  }

  return (
    <Collapsible
      title="Subjective"
      open={open}
      onOpenChange={(open) => {
        trackEvent(
          open ? TRACKERS.EXPAND_SUBJECTIVE : TRACKERS.COLLAPSE_SUBJECTIVE,
        );
        setOpen(open);
      }}
    >
      <div className="flex flex-col gap-2">
        {isEditing ? (
          <SubjectiveEditor
            onSubmit={onSubmit}
            onCancel={() => {
              trackEvent(TRACKERS.CANCEL_DOCUMENTING_SUBJECTIVE);
              setNoriAddendums([]);
              setAvCallAddendums([]);
              setIsEditing(false);
            }}
            isLoading={isLoading}
            documentReferences={data?.documentReferences}
            addendums={noriAddendums.map((a) => a.text)}
            prependAddendums={avCallAddendums.map((a) => a.text)}
            onAutoSave={handleAutoSave}
          />
        ) : isFetching ? (
          <div className="grid gap-2">
            {Array.from({ length: 3 }).map((_, i) => (
              //  biome-ignore lint/suspicious/noArrayIndexKey: unneeded for loading skeleton
              <Skeleton className="h-6" key={i} />
            ))}
          </div>
        ) : data?.documentReferences.length ? (
          <EditableRecordButton
            onClick={() => setIsEditing(true)}
            disabled={!canWriteSubjective}
          >
            {data?.documentReferences.map((documentReference) => {
              const { description, id } = documentReference;
              if (!description) {
                return (
                  <BodyText
                    size="sm"
                    textColor={canWriteSubjective ? "primary" : "tertiary"}
                    key={`${id}-${description}`}
                  >
                    Click to edit subjective
                  </BodyText>
                );
              }

              const content = updateSubjectiveSummaryTag(
                documentReference,
                visitTypeLabel,
              );
              return (
                <div key={`${id}-${description}`}>
                  {hasMarkdownFormatting(content) ? (
                    <MarkdownRenderer content={content} />
                  ) : (
                    <BodyText size="sm" preserveLineBreaks>
                      {content}
                    </BodyText>
                  )}
                </div>
              );
            })}
          </EditableRecordButton>
        ) : (
          <Button
            size="xs"
            onClick={handleClickAdd}
            className="w-fit"
            variant="secondary"
            disabled={!canWriteSubjective}
          >
            Add Subjective
          </Button>
        )}
        <AmazonCiq />
        <AmazonVisitHistory />
        {data?.documentReferences.length ? (
          <>
            <AVCallSummaryAddendums
              documentReferences={data.documentReferences}
              onAddAddendum={handleAddAvCallAddendum}
              addedAddendums={avCallAddendums}
              canWriteSubjective={canWriteSubjective}
            />
            <SubjectiveAddendums
              documentReferences={data.documentReferences}
              onAddAddendum={handleAddNoriAddendum}
              addedAddendums={noriAddendums}
              canWriteSubjective={canWriteSubjective}
            />
          </>
        ) : null}
      </div>
    </Collapsible>
  );
};
